'use client';
import React, { useEffect, useState } from 'react';
import { Box, SxProps, Typography } from '@mui/material';
import PageContainer from '../tableComponents/PageContainer';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import groupLogoUrl from '../../../static/product_image/YAGEO_Group_2024_KEMET_KEMET 7.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { PIMCORE_CONTENT_BASE_URL } from '../../../utils/Pimcore';
import { ReactComponent as SpecSheetIcon } from '../../../static/product_image/specsheetIcon.svg';

import { ReactComponent as DataSheetIcon } from '../../../static/product_image/datasheetIcon.svg';
import { fetchStockForPart } from './CompareInfoTable';
import { getImageUrl } from '../hooks/useProductTableColumns';
import { ProductsContent } from '../../../graphql/pimcore/modules/siteContent/types/ProductsContent';
import { formatCountIcuString } from '../../../utils/formatCount';
import { toUrl } from '@/utils/convertImagePath';

interface ProductCardProps {
    basePn: string;
    alias?: string;
    productImageUrl: string;
    stock: number;
    description: string;
    sx?: SxProps; // 可傳入自定義樣式
    dataSheet: string | undefined;
    specsheetSuppression: boolean | undefined;
    contentTree: ProductsContent;
    locale: 'en' | 'de' | 'ja' | 'zh-cn' | 'zh-tw';
}

const ProductCard: React.FC<ProductCardProps> = ({
    basePn,
    alias,
    productImageUrl,
    stock,
    description,
    sx,
    dataSheet,
    specsheetSuppression,
    contentTree,
    locale,
}) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { isScreenMd, isScreenSm, isScreenXs, responsiveWidth } = useResponsive();

    const handlePartNumberClick = (partNumber: string) => {
        const currentPath = location.pathname;
        navigate(`${currentPath}/part/${partNumber}`);
    };

    const mainTextSize = (() => {
        if (isScreenMd) return '24px';
        if (isScreenSm) return '20px';
        if (isScreenXs) return '18px';
        // 預設或 isScreenXs：
        return '18px';
    })();

    const groupImageWidth = (() => {
        if (isScreenMd) return '80px';
        if (isScreenSm) return '64px';
        if (isScreenXs) return '64px';
        // 預設或 isScreenXs：
        return '64px';
    })();

    const detailPadding = (() => {
        if (isScreenMd) return '8px 10px';
        return '8px 8px';
    })();

    const iconStyle = {
        display: 'inline-flex',
        cursor: 'pointer',
        '&:hover path': {
            fill: '#E5673E',
        },
        gap: '0.5rem',
    };

    const handleIconClick = (url: string, newTab: boolean = true) => {
        if (newTab) {
            window.open(url, '_blank');
        } else {
            window.location.href = url;
        }
    };

    const iconTextStyle = {
        fontSize: '14px',
        fontFamily: 'Poppins',
        fontWeight: 400,
    };

    const [octoStock, setStock] = useState<number>(stock);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchStockForPart(basePn);
                setStock(response); // Set the state with the resolved data
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };
        fetchData();
    }, [basePn]);

    return (
        <PageContainer
            containerName={`${basePn}-card`}
            sx={{
                backgroundColor: 'white',
                padding: 0,
                ...sx,
            }}
            innerSx={{
                width: responsiveWidth,
                height: 'calc(180px + 8px)',
                paddingTop: '8px',
                paddingBottom: '8px',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                }}
                data-testid={`${basePn}-full-card`}
            >
                {/* 左側：自動使用剩下的空間 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        maxWidth: isScreenXs ? '190px' : undefined,
                    }}
                    data-testid={`${basePn}-left`}
                >
                    {/* 上半部：產品標題與圖片 */}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <Typography
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontWeight: 700,
                                    fontSize: mainTextSize,
                                    lineHeight: '100%',
                                    color: '#B75332',
                                }}
                            >
                                {basePn}
                            </Typography>
                            {alias && (
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontWeight: 400,
                                        fontSize: '16px',
                                        lineHeight: '100%',
                                        color: '#B75332',
                                    }}
                                >
                                    ({alias})
                                </Typography>
                            )}
                            <Box
                                sx={{
                                    width: '120px',
                                    height: '70.28px',
                                    backgroundImage: `url(${getImageUrl(productImageUrl)})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundBlendMode: 'multiply',
                                }}
                                data-testid={'product-image'}
                            />
                        </Box>
                    </Box>
                    {/* 下半部：庫存數量與描述 */}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 500,
                                fontSize: '16px',
                                lineHeight: '24px',
                                color: '#0E8A13',
                            }}
                        >
                            {formatCountIcuString(
                                contentTree.filters.labels.resultCountCompact.content,
                                octoStock,
                                locale
                            )}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '18px',
                                color: '#191A1B',
                            }}
                        >
                            {description}
                        </Typography>
                    </Box>
                </Box>
                {/* 右側：永遠對齊 inner container 的最右邊 */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        marginLeft: 'auto',
                        height: '100%',
                    }}
                    data-testid={`${basePn}-right`}
                >
                    {/* 上方：群組 Logo */}
                    <Box
                        component="img"
                        src={toUrl(groupLogoUrl)}
                        alt="Group Logo"
                        sx={{
                            width: groupImageWidth,
                            height: '26px',
                            objectFit: 'contain',
                            transform: 'translateY(-6px)',
                        }}
                    />

                    {/* 中間：Icon 區塊，間距設小一點 */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '4px', // 調整間距值可讓兩個 icon 之間的空間變小
                            mt: 'auto', // 將 icon 區塊推到 Details 上方
                            mb: '8px',
                        }}
                    >
                        {/* Specsheet icon */}
                        {!specsheetSuppression && (
                            <Box
                                sx={iconStyle}
                                onClick={() =>
                                    handleIconClick(
                                        `https://search.kemet.com/component-documentation/download/specsheet/${basePn}`,
                                        true
                                    )
                                }
                                data-testid={`${basePn}-specsheet`}
                            >
                                <SpecSheetIcon title="Specsheet" />
                                <Typography sx={iconTextStyle}>
                                    {contentTree.product.specsheet.content}
                                </Typography>
                            </Box>
                        )}
                        {/* Datasheet icon */}
                        {dataSheet && (
                            <Box
                                sx={iconStyle}
                                onClick={() =>
                                    window.open(
                                        PIMCORE_CONTENT_BASE_URL +
                                            `/datasheet/asset/file/${encodeURIComponent(dataSheet)}`,
                                        '_blank'
                                    )
                                }
                                data-testid={`${basePn}-datasheet`}
                            >
                                <DataSheetIcon title="Datasheet" />
                                <Typography sx={iconTextStyle}>
                                    {contentTree.product.datasheet.content}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* 下方：Details 按鈕 */}
                    <Box
                        sx={{
                            backgroundColor: '#B75332',
                            borderRadius: '10px',
                            padding: detailPadding,
                            cursor: 'pointer',
                            width: '130px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}
                        onClick={() => handlePartNumberClick(basePn)}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Poppins',
                                fontWeight: 600,
                                fontSize: '16px',
                                lineHeight: '24px',
                                color: '#FFFFFF',
                            }}
                        >
                            {contentTree.product.productDetailsButtonLabel.content}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </PageContainer>
    );
};

export default ProductCard;
